import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Popup } from "semantic-ui-react";

import { CopyToClipboard, DATE_FORMATS_FNS, ReactTable } from "components";
import { EXACT_ACTIONS } from "models";
import "modules/ExactMonitoring/components/ApiErrors/components/ExactFailuresTable/ExactFailuresTable.scss";
import { PAGE_SIZE, enumToDropdownOptions } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { OnChangeFn, PaginationState } from "@tanstack/react-table";
import type { ExactOnlineFailure } from "models";

type ExactFailuresTableProps = {
  activePage: number;
  totalPages: number;
  totalItems: number;
  isLoading: boolean;
  isRefetching: boolean;
  failures?: ExactOnlineFailure[];
  onPageChange: (data: { activePage: number }) => void;
};

const columnHelper = createColumnHelper<ExactOnlineFailure>();

export const ExactFailuresTable = ({ failures, activePage, totalPages, onPageChange, totalItems, isRefetching, isLoading }: ExactFailuresTableProps) => {
  const { t } = useTranslation();

  const pagination = useMemo(() => ({ pageIndex: activePage - 1, pageSize: PAGE_SIZE.PageSize100 }), [activePage]);

  const handlePaginationChange: OnChangeFn<PaginationState> = updater => {
    const newState = typeof updater === "function" ? updater(pagination) : updater;
    onPageChange({ activePage: newState.pageIndex + 1 });
  };

  const actionOptions = useMemo(() => enumToDropdownOptions({ enumObj: EXACT_ACTIONS, t }), [t]);

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "exact_action",
        header: t("v8_exact_action", "Action"),
        cell: ({
          row: {
            original: { exact_action }
          }
        }) => {
          const actionKeyText = actionOptions.find(({ value }) => value === exact_action)?.text;
          return <Popup basic content={actionKeyText} trigger={<span>{actionKeyText}</span>} />;
        },
        size: 100
      }),
      columnHelper.accessor(row => row.status_code, {
        id: "status_code",
        header: t("v8_status_code", "Status Code"),
        size: 80
      }),
      columnHelper.accessor(row => row, {
        id: "dealer_name",
        header: t("v8_dealer", "Dealer"),
        cell: ({
          row: {
            original: { dealer_name }
          }
        }) => <Popup basic content={dealer_name} trigger={<span>{dealer_name}</span>} />,
        size: 80
      }),
      columnHelper.accessor(row => row, {
        id: "location_name",
        header: t("v8_location", "Location"),
        cell: ({
          row: {
            original: { location_name }
          }
        }) => <Popup basic content={location_name} trigger={<span>{location_name}</span>} />,
        size: 80
      }),
      columnHelper.accessor(row => row, {
        id: "request_body",
        header: t("v8_request_body", "Request Body"),
        cell: ({
          row: {
            original: { request_body }
          }
        }) => <CopyToClipboard text={request_body || ""} showPopup onSuccess={() => toast.success(t("v8_copy_to_clipboard", "Copied to clipboard"))} />,
        size: 350
      }),
      columnHelper.accessor(row => row, {
        id: "created_on",
        header: t("v8_date", "Date"),
        cell: ({
          row: {
            original: { created_on }
          }
        }) => formatDate(created_on, DATE_FORMATS_FNS.dateMonthYearTime),
        size: 100
      }),
      columnHelper.accessor(row => row, {
        id: "url",
        header: t("v8_url", "URL"),
        cell: ({
          row: {
            original: { url }
          }
        }) => <CopyToClipboard text={url} showPopup onSuccess={() => toast.success(t("v8_copy_to_clipboard", "Copied to clipboard"))} />,
        size: 250
      }),
      columnHelper.accessor(row => row, {
        id: "error_message",
        header: t("v8_error_message", "Error Message"),
        cell: ({
          row: {
            original: { errormessage }
          }
        }) => <CopyToClipboard text={errormessage ?? ""} showPopup onSuccess={() => toast.success(t("v8_copy_to_clipboard", "Copied to clipboard"))} />,
        size: 250
      })
    ],
    [t, actionOptions]
  );

  return (
    <div className="ExactFailuresTable">
      <ReactTable
        isFetching={isRefetching || isLoading}
        columns={columns}
        data={failures}
        pageCount={totalPages}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        totalItems={totalItems}
        pageSize={PAGE_SIZE.PageSize100}
      />
    </div>
  );
};
